/**
 * Weekly Prompt service, backed by Firestore.
 * A week's plan is a single doc: users/{uid}/weeklyPrompts/{weekStart}
 * holding { weekStart, weekEnd, promptIds } — reads/writes are one doc each.
 */

import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { auth, db } from '../../../../shared/firebase';

function requireUid() {
  const fbUser = auth.currentUser;
  if (!fbUser) throw new Error('Not signed in');
  return fbUser.uid;
}

/** Monday of the week containing `date`, as YYYY-MM-DD (mirrors old server). */
export function getWeekStart(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  const monday = new Date(d.setDate(diff));
  monday.setHours(0, 0, 0, 0);
  return monday.toISOString().split('T')[0];
}

/** Sunday of the week containing `date`, as YYYY-MM-DD. */
export function getWeekEnd(date = new Date()) {
  const startDate = new Date(getWeekStart(date));
  const sunday = new Date(startDate);
  sunday.setDate(startDate.getDate() + 6);
  return sunday.toISOString().split('T')[0];
}

const toResponse = (weekStart, data) => ({
  weekStart,
  prompts: (data?.promptIds || []).map((promptId) => ({
    promptId,
    weekStart,
    weekEnd: data?.weekEnd || null,
    createdAt: data?.createdAt || null,
  })),
});

/**
 * Get current week's prompts for user
 * @param {string} weekStart - Optional week start date (YYYY-MM-DD), defaults to current week
 * @returns {Promise<Object>} Object with weekStart and prompts array
 */
export const getCurrentWeekPrompts = async (weekStart = null) => {
  try {
    const uid = requireUid();
    const effectiveWeekStart = weekStart || getWeekStart();
    const snap = await getDoc(doc(db, 'users', uid, 'weeklyPrompts', effectiveWeekStart));
    return toResponse(effectiveWeekStart, snap.exists() ? snap.data() : null);
  } catch (error) {
    console.error('Error fetching weekly prompts:', error);
    throw error;
  }
};

/**
 * Save weekly prompts (replaces the week's plan)
 * @param {Array<string>} promptIds - Array of prompt IDs to save
 * @param {string} weekStart - Optional week start date (YYYY-MM-DD)
 * @param {string} weekEnd - Optional week end date (YYYY-MM-DD)
 * @returns {Promise<Object>} Object with weekStart and prompts array
 */
export const saveWeeklyPrompts = async (promptIds, weekStart = null, weekEnd = null) => {
  try {
    if (!promptIds || !Array.isArray(promptIds)) {
      throw new Error('promptIds array is required');
    }
    const uid = requireUid();
    const effectiveWeekStart = weekStart || getWeekStart();
    const effectiveWeekEnd = weekEnd || getWeekEnd();
    const data = {
      weekStart: effectiveWeekStart,
      weekEnd: effectiveWeekEnd,
      promptIds,
      createdAt: new Date().toISOString(),
    };
    await setDoc(doc(db, 'users', uid, 'weeklyPrompts', effectiveWeekStart), data);
    return toResponse(effectiveWeekStart, data);
  } catch (error) {
    console.error('Error saving weekly prompts:', error);
    throw error;
  }
};

/**
 * Clear current week's prompts
 * @param {string} weekStart - Optional week start date (YYYY-MM-DD), defaults to current week
 * @returns {Promise<Object>} Success response
 */
export const clearCurrentWeekPrompts = async (weekStart = null) => {
  try {
    const uid = requireUid();
    const effectiveWeekStart = weekStart || getWeekStart();
    await deleteDoc(doc(db, 'users', uid, 'weeklyPrompts', effectiveWeekStart));
    return { success: true, weekStart: effectiveWeekStart };
  } catch (error) {
    console.error('Error clearing weekly prompts:', error);
    throw error;
  }
};
